import { useMemo } from 'react'

import { cn } from '@/lib/utils'
import type { ActivityHeatmapDay } from '@/models/IActivity'
import {
  buildHeatmapGrid,
  formatDay,
  HEATMAP_LEVELS,
  WEEKDAY_LABELS
} from '@/utils/activity'

const SIZES = {
  default: { cell: 11, gap: 3 },
  compact: { cell: 9, gap: 2 }
}
const LEFT = 30
const TOP = 16

/** level -> fill (level 0 = no activity) */
const LEVEL_CLASSES = [
  'fill-muted/50',
  'fill-emerald-500/30',
  'fill-emerald-500/55',
  'fill-emerald-500/80',
  'fill-emerald-400'
]

const LEVEL_BG_CLASSES = [
  'bg-muted/50',
  'bg-emerald-500/30',
  'bg-emerald-500/55',
  'bg-emerald-500/80',
  'bg-emerald-400'
]

type ActivityHeatmapProps = {
  days: ActivityHeatmapDay[]
  from: string
  to: string
  /**
   * single-user heatmap: values are 0/1 on purpose, so only "active / not active" is shown
   * (no intensity levels, no legend scale)
   */
  binary?: boolean
  ariaLabel: string
  /** tooltip text of a day with a value */
  unit?: string
  /** smaller cells, e.g. for the user drawer */
  compact?: boolean
}

/** GitHub-style calendar heatmap, weeks as columns (Mon..Sun rows) */
export const ActivityHeatmap = ({
  days,
  from,
  to,
  binary,
  ariaLabel,
  unit = 'active users',
  compact
}: ActivityHeatmapProps) => {
  const { cell: CELL, gap: GAP } = SIZES[compact ? 'compact' : 'default']
  const grid = useMemo(() => buildHeatmapGrid(days, from, to), [days, from, to])
  const width = LEFT + grid.weeks.length * (CELL + GAP)
  const height = TOP + 7 * (CELL + GAP)

  const titleOf = (day: string, value: number) =>
    binary
      ? `${formatDay(day)}: ${value > 0 ? 'active' : 'not active'}`
      : `${formatDay(day)}: ${value} ${unit}`

  return (
    <div className="space-y-2 text-foreground">
      <div className="overflow-x-auto pb-1">
        <svg aria-label={ariaLabel} height={height} role="img" width={width}>
          {grid.months.map((month) => (
            <text
              className="fill-muted-foreground/60 text-[10px]"
              key={`${month.index}-${month.label}`}
              x={LEFT + month.index * (CELL + GAP)}
              y={10}
            >
              {month.label}
            </text>
          ))}
          {WEEKDAY_LABELS.map((label, i) =>
            i % 2 === 0 ? (
              <text
                className="fill-muted-foreground/60 text-[10px]"
                dominantBaseline="middle"
                key={label}
                x={0}
                y={TOP + i * (CELL + GAP) + CELL / 2}
              >
                {label}
              </text>
            ) : null
          )}
          {grid.weeks.map((week, w) =>
            week.map((cell, d) =>
              cell.outside ? null : (
                <rect
                  className={
                    LEVEL_CLASSES[
                      binary
                        ? cell.value > 0
                          ? HEATMAP_LEVELS - 1
                          : 0
                        : cell.level
                    ]
                  }
                  height={CELL}
                  key={cell.day}
                  rx={2}
                  width={CELL}
                  x={LEFT + w * (CELL + GAP)}
                  y={TOP + d * (CELL + GAP)}
                >
                  <title>{titleOf(cell.day, cell.value)}</title>
                </rect>
              )
            )
          )}
        </svg>
      </div>
      <div className="flex items-center justify-end gap-1 text-[11px] text-muted-foreground/70">
        {binary ? (
          <>
            <span
              className={cn('h-2.5 w-2.5 rounded-sm', LEVEL_BG_CLASSES[0])}
            />
            <span className="mr-2">not active</span>
            <span
              className={cn(
                'h-2.5 w-2.5 rounded-sm',
                LEVEL_BG_CLASSES[HEATMAP_LEVELS - 1]
              )}
            />
            <span>active</span>
          </>
        ) : (
          <>
            <span className="mr-1">Less</span>
            {LEVEL_BG_CLASSES.map((cls) => (
              <span className={cn('h-2.5 w-2.5 rounded-sm', cls)} key={cls} />
            ))}
            <span className="ml-1">More</span>
            {grid.max > 0 && (
              <span className="ml-2 tabular-nums">(max {grid.max})</span>
            )}
          </>
        )}
      </div>
    </div>
  )
}
